// Universal RecordTrail — shows Created/Modified/State transitions + audit link
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Clock, FileSearch, User } from "lucide-react";
import api from "@/lib/api";

const ROLE_BADGE = {
  system_admin: "bg-slate-200 text-slate-700",
  somturk_data_entry: "bg-sky-100 text-sky-700",
  somturk_observer: "bg-violet-100 text-violet-700",
  salihi_accountant: "bg-emerald-100 text-emerald-700",
  salihi_senior_accountant: "bg-emerald-200 text-emerald-800",
  salihi_finance_manager: "bg-amber-100 text-amber-700",
  salihi_compliance_officer: "bg-indigo-100 text-indigo-700",
  internal_auditor: "bg-rose-100 text-rose-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 19).replace("T", " "); }
}

function RoleBadge({ role }) {
  if (!role) return null;
  const cls = ROLE_BADGE[role] || "bg-slate-100 text-slate-600";
  const short = role.replace("salihi_", "").replace("somturk_", "").replace(/_/g, " ");
  return <Badge className={`${cls} border-0 text-[10px] font-medium`}>{short}</Badge>;
}

function Row({ label, email, role, at, comment }) {
  if (!email && !at) return null;
  return (
    <li className="flex items-start gap-3 py-2 border-l-2 border-[#4FA3D1] pl-3 ml-1">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
          <RoleBadge role={role} />
        </div>
        <p className="text-sm text-slate-800 mt-0.5">{email || "—"}</p>
        {comment && <p className="text-xs italic text-slate-600 mt-0.5">"{comment}"</p>}
      </div>
      <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(at)}</span>
    </li>
  );
}

/**
 * <RecordTrail entity_type="transaction" entity_id="abc" record={tx} />
 * `record` provides created_by/posted_by/etc. We also fetch the audit log
 * filtered by entity_id to enrich timestamps + roles where available.
 */
export default function RecordTrail({ entity_type, entity_id, record }) {
  const [audits, setAudits] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  useEffect(() => {
    if (!entity_id) return;
    api.get(`/audit-logs?entity_id=${entity_id}&page_size=50`).then(({ data }) => {
      setAudits(data.items || data || []);
    }).catch(() => {});
    api.get("/users").then(({ data }) => {
      const m = {}; (data || []).forEach(u => { m[u.email] = u.role; m[u.id] = u.role; });
      setUsersMap(m);
    }).catch(() => {});
  }, [entity_id]);

  const r = record || {};
  const role = (email) => usersMap[email] || null;

  // Build transition list from record fields
  const transitions = [
    { label: "Submitted by", email: r.submitted_by_email || r.prepared_by_email, at: r.submitted_at || r.prepared_at },
    { label: "Reviewed by", email: r.reviewed_by_email, at: r.reviewed_at, comment: r.review_comment },
    { label: "Verified by", email: r.verified_by_email, at: r.verified_at },
    { label: "Posted by", email: r.posted_by_email, at: r.posted_at },
    { label: "Approved by", email: r.approved_by_email, at: r.approved_at, comment: r.approval_comment },
    { label: "Rejected by", email: r.rejected_by_email || (r.status === "rejected" ? r.approved_by_email : null), at: r.rejected_at, comment: r.rejected_reason },
    { label: "Filed by", email: r.filed_by_email, at: r.filed_date },
    { label: "Paid by", email: r.paid_by_email, at: r.payment_date },
    { label: "Completed by", email: r.completed_by_email, at: r.completed_at },
  ].filter(t => t.email || t.at);

  return (
    <Card className="border border-slate-200 mt-4" data-testid="record-trail">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <FileSearch className="h-4 w-4 text-[#4FA3D1]" /> Record Trail
          </h3>
          {entity_id && (
            <Link to={`/audit-logs?entity_id=${entity_id}`} className="text-xs text-[#4FA3D1] hover:underline">
              View full audit trail →
            </Link>
          )}
        </div>
        <ul className="space-y-0.5">
          <Row label="Created by" email={r.created_by_email} role={role(r.created_by_email)} at={r.created_at} />
          {r.updated_at && r.updated_at !== r.created_at && (
            <Row label="Last modified" email={r.updated_by_email || r.created_by_email} role={role(r.updated_by_email || r.created_by_email)} at={r.updated_at} />
          )}
          {transitions.map((t, i) => (
            <Row key={i} label={t.label} email={t.email} role={role(t.email)} at={t.at} comment={t.comment} />
          ))}
          {audits.length > 0 && (
            <li className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-3 w-3 text-slate-400" />
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Audit entries ({audits.length})</span>
              </div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {audits.slice(0, 10).map((a, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-slate-600 gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <Badge variant="secondary" className="text-[10px]">{a.action}</Badge>
                      <span className="truncate">{a.user_email}</span>
                      <RoleBadge role={role(a.user_email)} />
                    </span>
                    <span className="whitespace-nowrap text-slate-400">{fmtDate(a.timestamp)}</span>
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}

/** Compact owner-column renderer for list pages. */
export function OwnerCell({ created_by_email, created_at, updated_by_email, updated_at }) {
  const initials = (created_by_email || "?").split("@")[0].split(/[._]/).map(s => s[0]?.toUpperCase()).slice(0, 2).join("");
  const tip = `Created by ${created_by_email || "—"} on ${fmtDate(created_at)}` +
              (updated_by_email && updated_by_email !== created_by_email
                ? `\nLast modified by ${updated_by_email} on ${fmtDate(updated_at)}` : "");
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={tip} data-testid="owner-cell">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#4FA3D1]/10 text-[#0B1929] text-[10px] font-semibold">{initials || <User className="h-3 w-3" />}</span>
      <span className="text-slate-500 hidden sm:inline">{(created_by_email || "—").split("@")[0]}</span>
    </span>
  );
}
